import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, X, Check, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Colour chip per urgency — mirrors the backend classifier so
// "expired" reads red even at a glance, "today" reads amber, and
// upcoming/normal reads neutral.
const urgencyChip = (urgency) => {
  if (urgency === 'expired') return 'bg-red-100 text-red-800 border-red-200';
  if (urgency === 'today') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const NotificationBell = () => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/count`);
      setCount(data.unread || 0);
    } catch (_e) { /* silent — bell is best-effort */ }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setItems(data || []);
    } catch (_e) {
      toast.error(t('auto.misc2.failedToLoadNotifications'));
    } finally { setLoading(false); }
  }, []);

  // Poll the counter every 60s so the badge stays roughly live
  // without the overhead of websockets. If the popover is open we
  // refetch the full list too so newly-arriving items appear.
  useEffect(() => {
    fetchCount();
    const t = setInterval(() => { fetchCount(); if (open) fetchItems(); }, 60000);
    return () => clearInterval(t);
  }, [fetchCount, fetchItems, open]);

  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

  const dismiss = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/dismiss`);
      setItems(prev => prev.filter(n => n.id !== id));
      setCount(c => Math.max(0, c - 1));
    } catch (_e) { toast.error(t('auto.misc2.failedToDismiss')); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setCount(0);
    } catch (_e) { toast.error(t('auto.misc2.failedToMarkAllRead')); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2"
          data-testid="notification-bell-btn"
          aria-label={t('auto.misc2.notifications')}
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-bell-count"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-bell-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-semibold text-sm text-slate-900">
            Notifications {count > 0 && <span className="text-slate-500 font-normal">({count} unread)</span>}
          </div>
          {items.some(n => !n.read) && (
            <button
              className="text-xs text-red-600 hover:underline"
              onClick={markAllRead}
              data-testid="notification-mark-all-read"
            >
              <Check className="inline w-3 h-3 mr-1" />{t('auto.misc2.markAllRead')}
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">{t('auto.misc2.loading')}</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              You&apos;re all caught up.
            </div>
          ) : items.map(n => (
            <div
              key={n.id}
              className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${n.read ? 'opacity-70' : ''}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase ${urgencyChip(n.urgency)}`}>
                      {n.urgency}
                    </span>
                    <span className="text-[11px] text-slate-500">as {n.recipient_tier}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
                  <div className="text-xs text-slate-500 whitespace-pre-line mt-1">{n.message}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {n.employee_id && (
                    <a
                      href={`/employees?id=${n.employee_id}`}
                      className="text-slate-400 hover:text-red-600"
                      title={t('auto.misc2.openEmployee')}
                      data-testid={`notification-open-employee-${n.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => dismiss(n.id)}
                    className="text-slate-400 hover:text-red-600"
                    title={t('auto.misc2.dismiss')}
                    data-testid={`notification-dismiss-${n.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
